import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { ExportData } from '../types';
import { formatAmount, spacer } from '../ksExporterUtils';
import { KITCHENSYNC_LOGO_BASE64 } from './logoData';

// Paper format configurations
const LETTER_COUNTRIES = ['US', 'USA', 'CA', 'CAN', 'MX', 'MEX', 'CL', 'CHL', 'PH', 'PHL'];

const PAPER_FORMATS = {
  LETTER: { format: 'letter', width: 215.9, height: 279.4 },
  A4: { format: 'a4', width: 210, height: 297 },
  LEGAL: { format: 'legal', width: 215.9, height: 355.6 },
  TABLOID: { format: 'tabloid', width: 279.4, height: 431.8 },
  A3: { format: 'a3', width: 297, height: 420 },
};

// Helper functions
const getCountryFromLocale = (locale: string | null): string | null => {
  if (!locale) return null;
  const parts = locale.split(/[-_]/);
  return parts.length > 1 ? parts[1].toUpperCase() : null;
};

const calculatePaperSize = (dataColumnCount: number = 0, isLandscape: boolean = false) => {
  try {
    const locales = {
      browser: navigator.language,
      system: Intl.DateTimeFormat().resolvedOptions().locale,
    };
    const countryCode = getCountryFromLocale(locales.browser) || getCountryFromLocale(locales.system);
    const useLetterSizes = LETTER_COUNTRIES.includes(countryCode || '');

    if (isLandscape) {
      if (dataColumnCount <= 8) {
        return useLetterSizes ? PAPER_FORMATS.LETTER : PAPER_FORMATS.A4;
      } else if (dataColumnCount <= 14) {
        return useLetterSizes ? PAPER_FORMATS.LEGAL : PAPER_FORMATS.A4;
      } else {
        return useLetterSizes ? PAPER_FORMATS.TABLOID : PAPER_FORMATS.A3;
      }
    } else {
      // Portrait - always use standard size since we split pages
      return useLetterSizes ? PAPER_FORMATS.LETTER : PAPER_FORMATS.A4;
    }
  } catch {
    return PAPER_FORMATS.A4;
  }
};

const formatPDFMoney = (value: number | string) => {
  if (value === null || value === undefined || value === '' || value === ' ' || value === 0) {
    return '';
  }
  return formatAmount(value as number, 2, true);
};

const formatPDFPercentage = (value: number | string) => {
  if (value === null || value === undefined || value === '' || value === ' ' || value === 0) {
    return '';
  }
  return formatAmount((value as number) * 100, 2, true, true) + '%';
};

// Abbreviate long location names for column headers
// Splits into multiple lines or truncates if too long
const abbreviateLocationName = (name: string, maxCharsPerLine: number = 12): string => {
  if (!name) return '';

  const upperName = name.toUpperCase();

  // If short enough, return as-is
  if (upperName.length <= maxCharsPerLine) {
    return upperName;
  }

  // Try to split by common separators (space, dash, comma)
  const words = upperName.split(/[\s\-,]+/);

  if (words.length > 1) {
    // Build lines with max chars per line
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if (currentLine === '') {
        currentLine = word;
      } else if ((currentLine + ' ' + word).length <= maxCharsPerLine) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }

    // Limit to 2 lines max
    if (lines.length > 2) {
      return lines.slice(0, 2).join('\n') + '...';
    }
    return lines.join('\n');
  }

  // Single long word - truncate with ellipsis
  return upperName.substring(0, maxCharsPerLine - 1) + '…';
};

interface ColumnGroup {
  key: string;
  colCount: number;
  hasSubTypes: boolean;
  isFixed: boolean;
}

interface FlattenedData {
  data: any[];
  columnGroups: ColumnGroup[];
  fixedColumnCount: number;
  dateColumnGroups: ColumnGroup[];
}

const flattenData = (data: any): FlattenedData => {
  const result: any[] = [];
  const headerRow1: any[] = [];
  const headerRow2: string[] = [];
  const columnGroups: ColumnGroup[] = [];

  data.forEach((cell: any, index: number) => {
    const firstRow = index === 0;
    const rowValues: string[] = [];

    for (const key in cell) {
      if (cell.hasOwnProperty(key)) {
        if (key !== 'style' && key !== 'budgetAvailable') {
          if (typeof cell[key] === 'object' && cell[key] !== null) {
            const hasActuals = 'actuals' in cell[key];
            const hasBudget = 'budget' in cell[key];
            const hasVariance = 'variance' in cell[key];

            if (hasActuals || hasBudget || hasVariance) {
              let subColumnCount = 0;

              if (hasActuals) {
                if ('money' in cell[key].actuals) {
                  if (firstRow) headerRow2.push(hasBudget ? 'Act $' : '$');
                  rowValues.push(formatPDFMoney(cell[key].actuals.money));
                  subColumnCount++;
                }
                if ('percent' in cell[key].actuals) {
                  if (firstRow) headerRow2.push(hasBudget ? 'Act %' : '%');
                  rowValues.push(formatPDFPercentage(cell[key].actuals.percent));
                  subColumnCount++;
                }
              }

              if (hasBudget) {
                if ('money' in cell[key].budget) {
                  if (firstRow) headerRow2.push('Bud $');
                  rowValues.push(formatPDFMoney(cell[key].budget.money));
                  subColumnCount++;
                }
                if ('percent' in cell[key].budget) {
                  if (firstRow) headerRow2.push('Bud %');
                  rowValues.push(formatPDFPercentage(cell[key].budget.percent));
                  subColumnCount++;
                }
              }

              if (hasVariance) {
                if ('money' in cell[key].variance) {
                  if (firstRow) headerRow2.push('Var $');
                  rowValues.push(formatPDFMoney(cell[key].variance.money));
                  subColumnCount++;
                }
                if ('percent' in cell[key].variance) {
                  if (firstRow) headerRow2.push('Var %');
                  rowValues.push(formatPDFPercentage(cell[key].variance.percent));
                  subColumnCount++;
                }
              }

              if (firstRow && subColumnCount > 0) {
                columnGroups.push({ key, colCount: subColumnCount, hasSubTypes: true, isFixed: false });
              }
            } else if ('money' in cell[key] || 'percent' in cell[key]) {
              let subColumnCount = 0;

              if ('money' in cell[key]) {
                if (firstRow) headerRow2.push('$');
                rowValues.push(formatPDFMoney(cell[key].money));
                subColumnCount++;
              }
              if ('percent' in cell[key]) {
                if (firstRow) headerRow2.push('%');
                rowValues.push(formatPDFPercentage(cell[key].percent));
                subColumnCount++;
              }

              if (firstRow && subColumnCount > 0) {
                columnGroups.push({ key, colCount: subColumnCount, hasSubTypes: true, isFixed: false });
              }
            }
          } else {
            if (firstRow) {
              const headerName =
                key === 'GL'
                  ? 'GL #'
                  : key === 'accounts'
                    ? 'DISTRIBUTION ACCOUNT'
                    : key === 'percentOf'
                      ? '% OF'
                      : key.toUpperCase();
              headerRow2.push(headerName);
              columnGroups.push({ key, colCount: 1, hasSubTypes: false, isFixed: true });
            }
            if (key === 'accounts') {
              rowValues.push(spacer(cell.style.level, '   ') + cell[key]);
            } else {
              rowValues.push(String(cell[key] || ''));
            }
          }
        }
      }
    }

    if (firstRow) {
      let headerRow2Index = 0;
      columnGroups.forEach(group => {
        if (group.hasSubTypes) {
          // Use abbreviated name for location columns to prevent overly wide columns
          headerRow1.push({
            content: abbreviateLocationName(group.key),
            colSpan: group.colCount,
            styles: { halign: 'center', fontStyle: 'bold' },
          });
          headerRow2Index += group.colCount;
        } else {
          const columnName = headerRow2[headerRow2Index];
          const isAccountColumn = columnName === 'DISTRIBUTION ACCOUNT';
          headerRow1.push({
            content: columnName,
            rowSpan: 2,
            styles: { halign: isAccountColumn ? 'left' : 'center', fontStyle: 'bold', valign: 'middle' },
          });
          headerRow2Index++;
        }
      });

      const filteredHeaderRow2: string[] = [];
      headerRow2Index = 0;
      columnGroups.forEach(group => {
        if (group.hasSubTypes) {
          for (let i = 0; i < group.colCount; i++) {
            filteredHeaderRow2.push(headerRow2[headerRow2Index]);
            headerRow2Index++;
          }
        } else {
          headerRow2Index++;
        }
      });

      result.push(headerRow1);
      result.push(filteredHeaderRow2);
    }
    result.push(rowValues);
  });

  const fixedColumnCount = columnGroups.filter(g => g.isFixed).reduce((sum, g) => sum + g.colCount, 0);
  const dateColumnGroups = columnGroups.filter(g => !g.isFixed);

  return { data: result, columnGroups, fixedColumnCount, dateColumnGroups };
};

// Generate footer text - matches QuickBooks format
const getFooterText = (): string => {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(now);
  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(now);
  const day = String(now.getDate()).padStart(2, '0');
  const year = now.getFullYear();
  const hours = String(now.getHours() % 12 || 12).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const ampm = now.getHours() >= 12 ? 'PM' : 'AM';
  const timezone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(now).split(' ').pop();

  return `Accrual Basis  ${weekday}, ${month} ${day}, ${year} ${hours}:${minutes} ${ampm} ${timezone}`;
};

// Draw page header and footer
const drawPageHeaderFooter = (
  doc: jsPDF,
  exportObject: ExportData,
  showHeaderOnAllPages: boolean,
  footerText: string,
  hookData: any
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Show header on page 1 always, or on all pages if showHeaderOnAllPages is true
  const isFirstPage = hookData.pageNumber === 1;
  const shouldShowHeader = isFirstPage || showHeaderOnAllPages;

  if (shouldShowHeader) {
    // Add logo to the top-left corner
    // Original logo is 509x56, scale to ~40mm width
    const logoWidth = 40;
    const logoHeight = (56 / 509) * logoWidth; // Maintain aspect ratio (~4.4mm)
    try {
      doc.addImage(KITCHENSYNC_LOGO_BASE64, 'PNG', 10, 5, logoWidth, logoHeight);
    } catch (e) {
      // Silently fail if logo can't be added
      console.warn('Failed to add logo to PDF:', e);
    }

    let headerY = 10;
    // Use title if available, otherwise fall back to type
    const reportTitle = exportObject.title || exportObject.type;
    if (reportTitle) {
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(reportTitle, pageWidth / 2, headerY, { align: 'center' });
      headerY += 6;
    }

    // Show consolidated legend if this is a consolidated report
    if (exportObject.isConsolidated) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Consolidated Report', pageWidth / 2, headerY, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      headerY += 5;
    }

    // Show location name(s) below the title
    // Use locationNames for multi-location reports, otherwise use single locationName
    const displayLocationName =
      exportObject.locationNames && exportObject.locationNames.length > 0
        ? exportObject.locationNames.join(', ')
        : exportObject.locationName;
    if (displayLocationName) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(displayLocationName, pageWidth / 2, headerY, { align: 'center' });
      headerY += 5;
    }
    if (exportObject.date?.range) {
      doc.setFontSize(10);
      doc.text(exportObject.date.range, pageWidth / 2, headerY, { align: 'center' });
    }
  }

  const footerY = pageHeight - 8;
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.setFont('helvetica', 'normal');
  doc.text(footerText, 10, footerY);
  doc.text(`${hookData.pageNumber}/${hookData.pageCount}`, pageWidth - 10, footerY, { align: 'right' });
  doc.setTextColor(0, 0, 0);
};

// Apply cell styling based on row data - matches QuickBooks PDF style
const applyCellStyles = (cellData: any, exportObject: ExportData, fixedColumnCount: number = 2) => {
  const { cell, row, column, section } = cellData;

  // Only apply custom styles to body cells, not headers
  if (section !== 'body') return;

  const rowData = exportObject.data[row.index];
  const style = rowData?.style;

  // Default: no borders for body cells (clean look)
  cell.styles.lineWidth = 0;

  if (!style) return;

  // Get account name to check for special rows
  const accountName = rowData?.accounts?.toLowerCase() || '';
  const isGrossProfit = accountName.includes('gross profit');
  const isNetIncome = accountName === 'net income';
  const isNetOperatingIncome = accountName.includes('net operating income');
  const isTotalFor = accountName.startsWith('total for');
  const isKeyTotal = isGrossProfit || isNetIncome || isNetOperatingIncome;

  // Level 0 headers (section headers like "Income", "Expenses") - bold
  if (style.level === 0 && !style.isTotal) {
    cell.styles.fontStyle = 'bold';
  }

  // "Total for..." rows - bold with thin line above
  if (style.isTotal || isTotalFor) {
    cell.styles.fontStyle = 'bold';
    cell.styles.lineColor = [0, 0, 0];
    cell.styles.lineWidth = { top: 0.2, bottom: 0, left: 0, right: 0 };
  }

  // Key totals (Gross Profit, Net Income, Net Operating Income) - bold with lines above and below
  if (isKeyTotal || (style as any).isGrandTotal || (style.level === 0 && style.isTotal)) {
    cell.styles.fontStyle = 'bold';
    cell.styles.lineColor = [0, 0, 0];
    cell.styles.lineWidth = { top: 0.3, bottom: 0.3, left: 0, right: 0 };
  }

  // Account column (usually index 1) should be left-aligned
  if (column.index < fixedColumnCount) {
    cell.styles.halign = 'left';
  }

  // Data columns should be right-aligned
  if (column.index >= fixedColumnCount) {
    cell.styles.halign = 'right';
  }
};

export interface PDFExportOptions {
  orientation?: 'portrait' | 'landscape';
  showHeaderOnAllPages?: boolean;
}

// ============================================
// LANDSCAPE PDF GENERATION
// ============================================
function generateLandscapePDF(exportObject: ExportData, showHeaderOnAllPages: boolean): jsPDF {
  const firstRow = exportObject.data[0];
  const metadataKeys = ['GL', 'accounts', 'percentOf', 'style', 'budgetAvailable'];
  const dataColumnCount = Object.keys(firstRow || {}).filter(key => !metadataKeys.includes(key)).length;

  const paperSize = calculatePaperSize(dataColumnCount, true);
  const doc = new jsPDF({
    format: paperSize.format,
    unit: 'mm',
    orientation: 'l',
  });

  const flatData = flattenData(exportObject.data);
  const footerText = getFooterText();
  const totalCols = flatData.data[1]?.length || 0;

  // Check if budget is enabled
  const hasBudget = exportObject.budgetAvailable === true;

  // When budget is enabled with more than 3 date columns, split into pages (3 date columns per page)
  // Otherwise render all columns on one page
  const needsPageSplit = hasBudget && flatData.dateColumnGroups.length > 3;
  const dateColumnsPerPage = 3;
  const pages = needsPageSplit ? splitDataIntoPages(flatData, dateColumnsPerPage) : [flatData.data];

  pages.forEach((pageData, pageIndex) => {
    if (pageIndex > 0) {
      doc.addPage();
    }

    const pageTotalCols = needsPageSplit ? pageData[1]?.length || 0 : totalCols;

    // Calculate sizes based on column count
    let baseFontSize = 9;
    let cellPadding = 2;
    let headerCellHeight = 8;
    let bodyCellHeight = 6;

    if (pageTotalCols > 15) {
      baseFontSize = 6;
      cellPadding = 1;
      headerCellHeight = 6;
      bodyCellHeight = 5;
    } else if (pageTotalCols > 12) {
      baseFontSize = 7;
      cellPadding = 1.5;
      headerCellHeight = 7;
      bodyCellHeight = 5.5;
    } else if (pageTotalCols > 9) {
      baseFontSize = 8;
      cellPadding = 1.5;
      headerCellHeight = 7;
      bodyCellHeight = 5.5;
    } else if (pageTotalCols > 6) {
      baseFontSize = 8.5;
      cellPadding = 2;
      headerCellHeight = 7.5;
      bodyCellHeight = 6;
    }

    // Build column styles
    const columnStyles: any = {};
    if (pageData[1]) {
      pageData[1].forEach((header: string, index: number) => {
        if (index === 0) {
          columnStyles[index] = { cellWidth: 'auto', halign: 'left', minCellWidth: 12 };
        } else if (index === 1) {
          columnStyles[index] = { cellWidth: 'auto', halign: 'left', minCellWidth: pageTotalCols > 10 ? 40 : 50 };
        } else if (header === '% OF') {
          columnStyles[index] = { cellWidth: 'auto', halign: 'left', minCellWidth: 15 };
        } else {
          columnStyles[index] = { cellWidth: 'auto', halign: 'right', minCellWidth: 16 };
        }
      });
    }

    (doc as any).autoTable({
      head: pageData.slice(0, 2),
      body: pageData.slice(2),
      startY: 32,
      margin: { top: 32, left: 10, right: 10, bottom: 15 },
      styles: {
        fontSize: baseFontSize,
        cellPadding: cellPadding,
        valign: 'middle' as const,
        fontStyle: 'normal' as const,
        overflow: 'linebreak' as const,
        cellWidth: 'wrap' as const,
        lineColor: [255, 255, 255] as [number, number, number],
        lineWidth: 0,
        font: 'helvetica',
        textColor: [0, 0, 0] as [number, number, number],
      },
      headStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
        fontStyle: 'bold' as const,
        textColor: [0, 0, 0] as [number, number, number],
        minCellHeight: headerCellHeight,
        fontSize: baseFontSize,
        halign: 'center' as const,
        lineColor: [0, 0, 0] as [number, number, number],
        lineWidth: { top: 0, bottom: 0.3, left: 0, right: 0 },
      },
      bodyStyles: {
        minCellHeight: bodyCellHeight,
        fillColor: [255, 255, 255] as [number, number, number],
        lineWidth: 0,
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
      },
      columnStyles: columnStyles,
      tableWidth: 'auto' as const,
      showHead: 'everyPage' as const,
      didDrawPage: (hookData: any) => {
        drawPageHeaderFooter(doc, exportObject, showHeaderOnAllPages, footerText, hookData);
      },
      didParseCell: (cellData: any) => {
        applyCellStyles(cellData, exportObject, flatData.fixedColumnCount);
      },
    });
  });

  return doc;
}

// ============================================
// PORTRAIT PDF GENERATION
// ============================================
function generatePortraitPDF(exportObject: ExportData, showHeaderOnAllPages: boolean): jsPDF {
  const firstRow = exportObject.data[0];
  const metadataKeys = ['GL', 'accounts', 'percentOf', 'style', 'budgetAvailable'];
  const dataColumnCount = Object.keys(firstRow || {}).filter(key => !metadataKeys.includes(key)).length;

  const paperSize = calculatePaperSize(dataColumnCount, false);
  const doc = new jsPDF({
    format: paperSize.format,
    unit: 'mm',
    orientation: 'p',
  });

  const flatData = flattenData(exportObject.data);
  const footerText = getFooterText();

  // Check if budget is enabled using the budgetAvailable flag from export data
  const hasBudget = exportObject.budgetAvailable === true;

  // Split data into pages - 2 date columns when budget enabled (4 sub-columns each), 7 otherwise
  const dateColumnsPerPage = hasBudget ? 2 : 7;
  const pages = splitDataIntoPages(flatData, dateColumnsPerPage);

  pages.forEach((pageData, pageIndex) => {
    if (pageIndex > 0) {
      doc.addPage();
    }

    const totalCols = pageData[1]?.length || 0;

    // Calculate sizes for portrait - optimized for 7+ months per page
    let baseFontSize = 8;
    let cellPadding = 1.5;
    let headerCellHeight = 7;
    let bodyCellHeight = 5;

    if (totalCols > 10) {
      baseFontSize = 7;
      cellPadding = 1;
      headerCellHeight = 6;
      bodyCellHeight = 4.5;
    } else if (totalCols > 8) {
      baseFontSize = 7.5;
      cellPadding = 1.2;
      headerCellHeight = 6.5;
      bodyCellHeight = 4.8;
    } else if (totalCols <= 5) {
      baseFontSize = 9;
      cellPadding = 2;
      headerCellHeight = 8;
      bodyCellHeight = 6;
    }

    // Build column styles for this page - optimized widths for QuickBooks style
    const columnStyles: any = {};
    if (pageData[1]) {
      pageData[1].forEach((header: string, index: number) => {
        if (index === 0) {
          // GL # column
          columnStyles[index] = { cellWidth: 'auto', halign: 'left', minCellWidth: 10 };
        } else if (index === 1) {
          // Distribution Account column - wider for better readability
          const accountWidth = hasBudget ? 50 : totalCols > 8 ? 40 : 50;
          columnStyles[index] = { cellWidth: 'auto', halign: 'left', minCellWidth: accountWidth };
        } else if (header === '% OF') {
          columnStyles[index] = { cellWidth: 'auto', halign: 'left', minCellWidth: 12 };
        } else if (header.includes('%')) {
          // Percentage columns (Act %, Bud %, Var %) - wider to prevent text wrapping
          columnStyles[index] = { cellWidth: 'auto', halign: 'right', minCellWidth: 18 };
        } else {
          // Data columns (money columns)
          const dataColWidth = hasBudget ? 14 : totalCols > 8 ? 14 : 16;
          columnStyles[index] = { cellWidth: 'auto', halign: 'right', minCellWidth: dataColWidth };
        }
      });
    }

    (doc as any).autoTable({
      head: pageData.slice(0, 2),
      body: pageData.slice(2),
      startY: 32,
      margin: { top: 32, left: 8, right: 8, bottom: 15 },
      styles: {
        fontSize: baseFontSize,
        cellPadding: cellPadding,
        valign: 'middle' as const,
        fontStyle: 'normal' as const,
        overflow: 'linebreak' as const,
        cellWidth: 'wrap' as const,
        lineColor: [255, 255, 255] as [number, number, number],
        lineWidth: 0,
        font: 'helvetica',
        textColor: [0, 0, 0] as [number, number, number],
      },
      headStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
        fontStyle: 'bold' as const,
        textColor: [0, 0, 0] as [number, number, number],
        minCellHeight: headerCellHeight,
        fontSize: baseFontSize,
        halign: 'center' as const,
        lineColor: [0, 0, 0] as [number, number, number],
        lineWidth: { top: 0, bottom: 0.3, left: 0, right: 0 },
      },
      bodyStyles: {
        minCellHeight: bodyCellHeight,
        fillColor: [255, 255, 255] as [number, number, number],
        lineWidth: 0,
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255] as [number, number, number],
      },
      columnStyles: columnStyles,
      tableWidth: 'auto' as const,
      showHead: 'everyPage' as const,
      didDrawPage: (hookData: any) => {
        drawPageHeaderFooter(doc, exportObject, showHeaderOnAllPages, footerText, hookData);
      },
      didParseCell: (cellData: any) => {
        applyCellStyles(cellData, exportObject, flatData.fixedColumnCount);
      },
    });
  });

  return doc;
}

// Split data into pages for portrait mode
function splitDataIntoPages(flatData: FlattenedData, dateColumnsPerPage: number = 2): any[][] {
  const { data, fixedColumnCount, dateColumnGroups } = flatData;

  if (dateColumnGroups.length <= dateColumnsPerPage) {
    return [data];
  }

  const pages: any[][] = [];
  let currentIndex = fixedColumnCount;
  const dateGroupRanges: { start: number; end: number; group: ColumnGroup }[] = [];

  dateColumnGroups.forEach(group => {
    dateGroupRanges.push({ start: currentIndex, end: currentIndex + group.colCount, group });
    currentIndex += group.colCount;
  });

  for (let i = 0; i < dateGroupRanges.length; i += dateColumnsPerPage) {
    const pageGroups = dateGroupRanges.slice(i, i + dateColumnsPerPage);
    const pageData: any[] = [];

    data.forEach((row, rowIndex) => {
      if (rowIndex === 0) {
        const newHeaderRow1: any[] = [];
        const fixedHeaders: any[] = [];
        const dateHeaders: any[] = [];

        row.forEach((cell: any) => {
          if (cell && typeof cell === 'object' && cell.rowSpan === 2) {
            fixedHeaders.push(cell);
          } else if (cell && typeof cell === 'object' && cell.colSpan) {
            dateHeaders.push(cell);
          }
        });

        fixedHeaders.forEach(h => newHeaderRow1.push(h));
        pageGroups.forEach(range => {
          const dateHeaderIdx = dateGroupRanges.indexOf(range);
          if (dateHeaderIdx >= 0 && dateHeaders[dateHeaderIdx]) {
            newHeaderRow1.push(dateHeaders[dateHeaderIdx]);
          }
        });

        pageData.push(newHeaderRow1);
      } else if (rowIndex === 1) {
        const newHeaderRow2: string[] = [];
        pageGroups.forEach(range => {
          for (let c = range.start; c < range.end; c++) {
            const adjustedIdx = c - fixedColumnCount;
            if (row[adjustedIdx] !== undefined) {
              newHeaderRow2.push(row[adjustedIdx]);
            }
          }
        });
        pageData.push(newHeaderRow2);
      } else {
        const newRow: string[] = [];
        for (let c = 0; c < fixedColumnCount; c++) {
          newRow.push(row[c]);
        }
        pageGroups.forEach(range => {
          for (let c = range.start; c < range.end; c++) {
            newRow.push(row[c]);
          }
        });
        pageData.push(newRow);
      }
    });

    pages.push(pageData);
  }

  return pages;
}

// ============================================
// MAIN PREVIEW FUNCTION
// ============================================
function generatePDFPreview(exportObject: ExportData, options?: PDFExportOptions): string {
  const showHeaderOnAllPages = options?.showHeaderOnAllPages !== false;
  const orientation = options?.orientation || 'landscape';
  const isLandscape = orientation === 'landscape';

  const doc = isLandscape
    ? generateLandscapePDF(exportObject, showHeaderOnAllPages)
    : generatePortraitPDF(exportObject, showHeaderOnAllPages);

  // Return base64 string for PDF preview
  return doc.output('datauristring');
}

export { generatePDFPreview };
